import { useRef } from 'react';
import { useStableCallbackCollection } from 'utils/hooks/useStableCallbackCollection';

/**
 * Collect refs to a collection of elements
 *
 * The map doesn't contain null refs. Unmounted elements are removed from the list.
 *
 * It seems intuitive that ref keys passed to setDifRef match the React key attribute.
 * However, it is not a requirement, the hook works correctly with any kind of refs map keys
 * (they just must obviously be unique, otherwise the later occurrence of the same key
 * will overwrite previous one). Proper function of ref map keys different from React key attribute
 * is verified by a test.
 *
 * @example
 * const [divRefs, setDivRef] = useElementListRef();
 *
 * useEffect(() => {
 *   // do anything with the Map of refs provided
 *   divRefs.get('someFooId').style.display = 'none';
 *   Array.from(divRefs.values() ?? []).forEach(el => {
 *     console.log(el.offsetTop);
 *   });
 * });
 *
 * return foos.map((foo => <div key={foo.id} ref={setDivRef(foo.id)}>...</div>}
 */
export const useElementListRef = <TKey, TElement = HTMLElement>(): [
  refs: Map<TKey, TElement>,
  setRef: (index: TKey) => (el: TElement | null) => void,
] => {
  const refs = useRef(new Map<TKey, TElement>());
  const setRef = useStableCallbackCollection(
    (key: TKey) =>
      (el: TElement | null): void => {
        if (el === null) {
          refs.current.delete(key);
        } else {
          refs.current.set(key, el);
        }
      },
  );
  return [refs.current, setRef];
};
